import type { ResponseByPath, Schemas } from '@@/api/types'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { useApi } from '@/composables/useApi.js'
import useTranslation from '@/composables/useTranslation.js'

export type LabelPrintingSolution =
  (typeof LabelPrintingSolutions)[keyof typeof LabelPrintingSolutions]
export const LabelPrintingSolutions = {
  Disabled: 'DISABLED',
  Blackbox: 'BLACKBOX',
  BlackboxAdvanced: 'BLACKBOX_ADVANCED',
  Pdf: 'PDF',
  Windows: 'WINDOWS',
  Zebra: 'ZEBRA',
} as const

export type LabelDetails = {
  line_1?: string | null
  line_2?: string | null
  line_3?: string | null
  line_4?: string | null
  line_5?: string | null
  text?: string | null
  header?: string | null
  body?: string | null
  footer?: string | null
  previewAvailable: boolean
  amount: number
  deviceId: string
}

const BUILD_LABEL_URL = '/{provet_id}/api/labels/build'
const PREVIEW_LABEL_URL = '/{provet_id}/api/labels/preview'
const PRINT_LABEL_URL = '/{provet_id}/api/labels/print'
const ACTIVE_LABEL_DEVICES_URL =
  '/{provet_id}/api/departments/{id}/label_devices/active'

export type BuildLabelResponse = ResponseByPath<'post', typeof BUILD_LABEL_URL>
export type PreviewLabelResponse = ResponseByPath<
  'post',
  typeof PREVIEW_LABEL_URL
>
export type PrintLabelResponse = ResponseByPath<'post', typeof PRINT_LABEL_URL>
export type LabelDevicesResponse = ResponseByPath<
  'get',
  typeof ACTIVE_LABEL_DEVICES_URL
>

export type LabelDevice = Schemas['LabelDevice']
export type Base64Document = Schemas['Base64Document']
export type Label = Schemas['Label']
export type LabelObjectType = NonNullable<
  Schemas['Label']['attributes']
>['object_type']

type PrepareLabelRequestBodyParams = {
  dataType: 'Base64Document' | 'Label'
  objectId: string
  objectType: LabelObjectType
  departmentId?: string
  labelDetails?: Partial<LabelDetails>
}
type LabelQueryParameters = {
  objectId: string
  objectType: LabelObjectType
  departmentId?: string
  labelDetails?: Partial<LabelDetails>
  enabled?: MaybeRef<boolean>
}

const prepareLabelRequestBody = ({
  dataType,
  objectId,
  objectType,
  departmentId,
  labelDetails,
}: PrepareLabelRequestBodyParams) => {
  const { deviceId, amount = 1, ...attributes } = labelDetails || {}
  let relationships = {}
  if (departmentId) {
    relationships = {
      department: {
        data: {
          type: 'Department',
          id: departmentId,
        },
      },
    }
  }
  if (deviceId) {
    relationships = {
      ...relationships,
      device: {
        data: {
          type: 'LabelDevice',
          id: deviceId,
        },
      },
    }
  }
  return {
    data: {
      type: dataType,
      attributes: {
        object_type: objectType,
        object_id: Number(objectId),
        amount,
        ...attributes,
      },
      relationships,
    },
  }
}

export function useBuildLabelMutation() {
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      objectId,
      objectType,
      departmentId,
      labelDetails,
    }: LabelQueryParameters) =>
      api((client) =>
        client.POST(BUILD_LABEL_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: prepareLabelRequestBody({
            dataType: 'Label',
            objectId,
            objectType,
            departmentId,
            labelDetails,
          }) as Schemas['LabelRequest'],
        }),
      ),
  })
}

export function usePreviewLabelMutation() {
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      objectId,
      objectType,
      departmentId,
      labelDetails,
    }: LabelQueryParameters) =>
      api((client) => {
        // API has a requirement, that type in request and response should match
        // So we send Label structure, but set type to Base64Document
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return client.POST(PREVIEW_LABEL_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: prepareLabelRequestBody({
            dataType: 'Base64Document',
            objectId,
            objectType,
            departmentId,
            labelDetails,
          }) as any,
        })
        /* eslint-enable @typescript-eslint/no-explicit-any */
      }),
  })
}

export function usePrintLabelMutation() {
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      objectId,
      objectType,
      departmentId,
      labelDetails,
    }: LabelQueryParameters) =>
      api((client) => {
        // API has a requirement, that type in request and response should match
        // So we send Label structure, but set type to Base64Document
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return client.POST(PRINT_LABEL_URL, {
          params: { path: { provet_id: route.params.provetId } },
          body: prepareLabelRequestBody({
            dataType: 'Base64Document',
            objectId,
            objectType,
            departmentId,
            labelDetails,
          }) as any,
        })
        /* eslint-enable @typescript-eslint/no-explicit-any */
      }),
  })
}

export const labelDeviceKeys = {
  all: ['labelDevices'] as const,
  list: (departmentId: string) =>
    [...labelDeviceKeys.all, 'list', departmentId] as const,
  listActive: (departmentId: string) =>
    [...labelDeviceKeys.all, 'listActive', departmentId] as const,
}

export function useActiveLabelDevicesQuery(
  departmentId: string,
  enabled = true,
) {
  const api = useApi()
  const route = useRoute()
  const tPage = useTranslation('common.labelPrint.modal.form')
  const getDeviceLabel = (device: LabelDevice) => {
    const name = device.attributes?.name || ''
    let deviceType
    switch (device.attributes?.device_type) {
      case LabelPrintingSolutions.Blackbox:
        deviceType = tPage('deviceTypes.blackbox')
        break
      case LabelPrintingSolutions.BlackboxAdvanced:
        deviceType = tPage('deviceTypes.blackbox_advanced')
        break
      case LabelPrintingSolutions.Windows:
        deviceType = tPage('deviceTypes.windows')
        break
      case LabelPrintingSolutions.Zebra:
        deviceType = tPage('deviceTypes.zebra')
        break
      case LabelPrintingSolutions.Pdf:
        deviceType = tPage('deviceTypes.pdf')
        break
      default:
        break
    }
    if (deviceType) {
      return `${deviceType}: ${name}`
    }
    return name
  }
  const query = useQuery({
    queryKey: labelDeviceKeys.listActive(departmentId),
    enabled,
    queryFn: async () => {
      return await api((client) =>
        client.GET(ACTIVE_LABEL_DEVICES_URL, {
          params: {
            path: { provet_id: route.params.provetId, id: departmentId },
          },
        }),
      )
    },
    select: (data: LabelDevicesResponse) => {
      return data.data.map((device: LabelDevice) => ({
        value: device.id,
        label: getDeviceLabel(device),
      }))
    },
  })

  return reactive(query)
}
